import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import FeatureFolder from '../ui/FeatureFolder';

const features = [
  { icon: 'FiLayers', title: 'One Technology Partner', desc: 'All your digital needs under one roof.', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
  { icon: 'FiServer', title: 'Modern Infrastructure', desc: 'Built on cutting-edge cloud technology.', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
  { icon: 'FiCamera', title: 'HostFig Studio', desc: 'Professional catalog and model-like imagery.', gradient: 'linear-gradient(135deg, #ec4899, #db2777)' },
  { icon: 'FiZap', title: 'Fast Deployment', desc: 'Launch your projects faster than ever.', gradient: 'linear-gradient(135deg, #f59e0b, #ea580c)' },
  { icon: 'FiLifeBuoy', title: 'Reliable Support', desc: '24/7 expert support whenever you need.', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
  { icon: 'FiShield', title: 'Enterprise Security', desc: 'Top-tier security to protect your business.', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
  { icon: 'FiMaximize', title: 'Scalable Solutions', desc: 'Grow your business without limitations.', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)' },
  { icon: 'FiTag', title: 'Transparent Pricing', desc: 'No hidden costs, no surprises.', gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)' }
];

const groupedSteps = [
  {
    phase: "01",
    title: "Strategy & Design",
    items: [
      { label: 'Discover', desc: 'We understand your needs and analyze your business goals to establish a clear baseline.', iconName: 'Search' },
      { label: 'Plan', desc: 'We create a strategic plan and architecture for your digital infrastructure.', iconName: 'Map' },
      { label: 'Design', desc: 'We design with purpose, crafting interfaces that align with your brand identity.', iconName: 'PenTool' }
    ]
  },
  {
    phase: "02",
    title: "Engineering",
    items: [
      { label: 'Develop', desc: 'We build with precision utilizing cutting-edge, scalable technologies.', iconName: 'Code' },
      { label: 'Test', desc: 'Rigorous quality assurance ensures flawless performance across all devices.', iconName: 'CheckCircle' },
      { label: 'Deploy', desc: 'We launch seamlessly, transitioning your project to our enterprise cloud.', iconName: 'Send' }
    ]
  },
  {
    phase: "03",
    title: "Growth",
    items: [
      { label: 'Launch', desc: 'Your project goes live with a carefully orchestrated rollout to maximize impact.', iconName: 'Rocket' },
      { label: 'Support', desc: 'We support your growth with 24/7 expert technical assistance.', iconName: 'LifeBuoy' },
      { label: 'Update', desc: 'We consistently optimize and scale your infrastructure as you expand.', iconName: 'TrendingUp' }
    ]
  }
];

const FeaturesProcess = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section id="section-features" className="relative py-24 bg-white border-t border-slate-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Why Choose Us - 3D Folders */}
        <div className="mb-32">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight">Why Choose HostFig?</h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-sm md:text-base">Hover over the folders to reveal what makes our digital infrastructure the best choice for your business.</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-12 justify-items-center max-w-5xl mx-auto">
            {features.map((feat, i) => (
              <FeatureFolder key={i} feature={feat} />
            ))}
          </div>
        </div>

        {/* Our Process - Tabbed Interface */}
        <div className="relative">
          <div className="text-center mb-12 relative z-10">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight">Our Process</h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-sm md:text-base">A transparent, streamlined approach from initial discovery to final deployment and beyond.</p>
          </div>

          {/* Desktop Tabs */}
          <div className="hidden md:flex flex-row gap-4 mb-8 max-w-5xl mx-auto">
            {groupedSteps.map((step, idx) => (
              <button 
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`flex-1 flex items-center gap-4 p-4 rounded-2xl border transition-all duration-300 ${
                  activeTab === idx 
                    ? 'bg-white border-blue-600 shadow-lg ring-1 ring-blue-600 scale-[1.02] z-10' 
                    : 'bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg transition-colors duration-300 ${
                  activeTab === idx ? 'bg-brand-gradient text-white shadow-md' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step.phase}
                </div>
                <span className={`font-bold tracking-wide uppercase text-sm transition-colors duration-300 ${
                  activeTab === idx ? 'text-brand-gradient' : 'text-slate-500'
                }`}>
                  {step.title}
                </span>
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="max-w-5xl mx-auto bg-white rounded-[2rem] border border-slate-200 shadow-xl overflow-hidden p-6 md:p-10 lg:p-12 min-h-[500px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center h-full"
              >
                <div className="hidden md:flex relative aspect-square md:aspect-[4/3] bg-slate-50 rounded-3xl border border-slate-100 overflow-hidden items-center justify-center p-6 sm:p-8">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent pointer-events-none"></div>
                  <div className="grid grid-cols-2 gap-4 w-full relative z-10">
                    {groupedSteps[activeTab].items.map((item, i) => (
                      <motion.div 
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.1 + 0.2 }}
                        className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col gap-4"
                      >
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                          <SafeIcon name={item.iconName} className="w-5 h-5" />
                        </div>
                        <div className="space-y-2.5">
                          <div className="h-2.5 bg-slate-100 rounded-full w-full"></div>
                          <div className="h-2.5 bg-slate-100 rounded-full w-2/3"></div>
                        </div>
                      </motion.div>
                    ))}
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 3 * 0.1 + 0.2 }}
                      className="bg-white/40 p-5 rounded-2xl border border-slate-200 border-dashed flex flex-col gap-4 items-center justify-center"
                    >
                      <div className="w-10 h-10 rounded-full border-2 border-slate-300 border-dashed animate-[spin_4s_linear_infinite]"></div>
                      <div className="h-2.5 bg-slate-200/50 rounded-full w-1/2"></div>
                    </motion.div>
                  </div>
                </div>

                <div className="flex flex-col justify-center">
                  <div className="text-5xl md:text-6xl font-black text-blue-600 mb-2 opacity-90">{groupedSteps[activeTab].phase}</div>
                  <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-10">{groupedSteps[activeTab].title}</h3>
                  <div className="space-y-8">
                    {groupedSteps[activeTab].items.map((item, idx) => (
                      <motion.div 
                        key={idx} 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.15 + 0.2 }}
                        className="flex gap-5 group"
                      >
                        <div className="flex-shrink-0 w-8 h-8 mt-1 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-sm border border-blue-100 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                          {idx + 1}
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-slate-900 mb-2">{item.label}</h4>
                          <p className="text-slate-600 leading-relaxed text-sm md:text-base">{item.desc}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesProcess;